import {
  AlertCircleIcon,
  AlertTriangleIcon,
  LockIcon,
  RefreshCwIcon,
  SearchXIcon,
  ServerCrashIcon,
  WifiOffIcon,
} from 'lucide-react';

import React from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import { Alert, Button, Card } from '@splitfiesta/ui';

interface ErrorDisplayProps {
  error: string | Error;
  onRetry?: () => void;
  type?: 'inline' | 'page' | 'card';
  showIcon?: boolean;
}

export function ErrorDisplay({
  error,
  onRetry,
  type = 'inline',
  showIcon = true,
}: ErrorDisplayProps) {
  const { t } = useTranslation();

  const errorMessage = error instanceof Error ? error.message : error;

  // Determine error type and icon
  let icon = <AlertTriangleIcon className="w-5 h-5" />;
  let title = t('common:errors.genericError');
  let description = errorMessage;

  if (
    errorMessage.toLowerCase().includes('network') ||
    errorMessage.toLowerCase().includes('fetch')
  ) {
    icon = <WifiOffIcon className="w-5 h-5" />;
    title = t('common:errors.networkError');
    description = t('common:errors.networkErrorDesc');
  } else if (
    errorMessage.toLowerCase().includes('server') ||
    errorMessage.toLowerCase().includes('500')
  ) {
    icon = <ServerCrashIcon className="w-5 h-5" />;
    title = t('common:errors.serverError');
    description = t('common:errors.serverErrorDesc');
  } else if (
    errorMessage.toLowerCase().includes('unauthorized') ||
    errorMessage.toLowerCase().includes('401')
  ) {
    icon = <LockIcon className="w-5 h-5" />;
    title = t('common:errors.unauthorizedError');
    description = t('common:errors.unauthorizedErrorDesc');
  } else if (
    errorMessage.toLowerCase().includes('not found') ||
    errorMessage.toLowerCase().includes('404')
  ) {
    icon = <SearchXIcon className="w-5 h-5" />;
    title = t('common:errors.notFoundError');
    description = t('common:errors.notFoundErrorDesc');
  } else if (errorMessage.toLowerCase().includes('validation')) {
    icon = <AlertCircleIcon className="w-5 h-5" />;
    title = t('common:errors.validationError');
    description = errorMessage;
  }

  if (type === 'inline') {
    return (
      <Alert
        type="error"
        message={
          <div
            className="flex items-start justify-between w-full"
            data-testid="error-display-inline"
          >
            <span data-testid="error-message">{description}</span>
            {onRetry && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRetry}
                aria-label={t('common:errors.retry')}
                className="ml-3 text-error-600 hover:text-error-800 p-1"
                data-testid="error-retry-button"
              >
                <RefreshCwIcon className="w-4 h-4" />
              </Button>
            )}
          </div>
        }
        icon={showIcon ? () => icon : undefined}
      />
    );
  }

  if (type === 'card') {
    return (
      <Card
        className="bg-error-50 border-error-200"
        data-testid="error-display-card"
      >
        <div className="flex items-start">
          {showIcon && (
            <div className="shrink-0 text-error-500" data-testid="error-icon">
              {icon}
            </div>
          )}
          <div className="ml-3 flex-1">
            <h3
              className="text-sm font-medium text-error-800"
              data-testid="error-title"
            >
              {title}
            </h3>
            <p
              className="mt-1 text-sm text-error-700"
              data-testid="error-description"
            >
              {description}
            </p>
            {onRetry && (
              <Button
                onClick={onRetry}
                variant="ghost"
                size="sm"
                leftIcon={<RefreshCwIcon className="w-4 h-4" />}
                className="mt-3 text-error-600 hover:text-error-800"
                data-testid="error-retry-button"
              >
                {t('common:errors.retry')}
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  }

  // Page type
  return (
    <div
      className="min-h-[400px] flex items-center justify-center"
      data-testid="error-display-page"
    >
      <div className="text-center">
        <div
          className="flex justify-center mb-4 text-error-500"
          data-testid="error-icon-large"
        >
          {icon && React.cloneElement(icon, { className: 'w-12 h-12' })}
        </div>
        <h2
          className="text-2xl font-semibold text-gray-900 mb-2"
          data-testid="error-title-large"
        >
          {title}
        </h2>
        <p
          className="text-gray-600 mb-6 max-w-md"
          data-testid="error-description-large"
        >
          {description}
        </p>
        {onRetry && (
          <Button
            onClick={onRetry}
            variant="primary"
            leftIcon={<RefreshCwIcon className="w-4 h-4" />}
            data-testid="error-retry-button-large"
          >
            {t('common:errors.tryAgain')}
          </Button>
        )}
      </div>
    </div>
  );
}

// Specific error components
export function NetworkError({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div data-testid="network-error">
      <ErrorDisplay
        error={t('common:errors.networkError')}
        onRetry={onRetry}
        type="page"
      />
    </div>
  );
}

export function NotFoundError({ message }: { message?: string }) {
  const { t } = useTranslation();
  return (
    <div data-testid="not-found-error">
      <ErrorDisplay
        error={message || t('common:errors.notFoundErrorDesc')}
        type="page"
      />
    </div>
  );
}

export function UnauthorizedError() {
  const { t } = useTranslation();
  return (
    <div data-testid="unauthorized-error">
      <ErrorDisplay
        error={t('common:errors.unauthorizedErrorDesc')}
        type="page"
      />
    </div>
  );
}
